
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { calculateMonthData, getVisibilityRating } from '@/utils/calendarData';

interface Location {
  id?: string;
  name: string;
  latitude: number;
  longitude: number;
  created_at?: string;
}

interface YearlyCalendarViewProps {
  location: Location;
  year: number;
  yearlyActiveMonth: number;
  setYearlyActiveMonth: (month: number) => void;
  isSubscribed?: boolean;
}

interface DayInfo {
  day: number;
  rating: number;
}

// Generate all days for a month with their ratings using real astronomical calculations
const getAllDaysForMonth = (year: number, month: number, location: Location): DayInfo[] => {
  const monthData = calculateMonthData(
    { name: location.name, latitude: location.latitude, longitude: location.longitude },
    month,
    year
  );
  
  return monthData.map(day => ({
    day: day.date,
    rating: getVisibilityRating(day),
  }));
};

// Get the day of week (0-6, Sunday-Saturday) for the first day of a month
const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};

// Get color class based on visibility rating
const getRatingColorClass = (rating: number): string => {
  if (rating >= 8) return 'bg-emerald-600 text-white';
  if (rating >= 6) return 'bg-green-500 text-foreground font-medium';
  if (rating >= 4) return 'bg-lime-400 text-foreground font-medium';
  if (rating >= 2) return 'bg-amber-300 text-foreground font-medium';
  return 'text-muted-foreground/50';
};

export const YearlyCalendarView: React.FC<YearlyCalendarViewProps> = ({
  location,
  year,
}) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  // Calculate data for all 12 months
  const yearlyData = useMemo(() => {
    return monthNames.map((name, index) => ({
      name,
      index,
      days: getAllDaysForMonth(year, index, location),
      firstDayOfWeek: getFirstDayOfMonth(year, index),
    }));
  }, [year, location.latitude]);

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 p-4 bg-card rounded-lg border">
        <span className="text-sm font-medium text-muted-foreground">Visibility:</span>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-600"></div>
          <span className="text-xs">Excellent (8-10)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500"></div>
          <span className="text-xs">Very Good (6-7)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-lime-400"></div>
          <span className="text-xs">Good (4-5)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-amber-300"></div>
          <span className="text-xs">Fair (2-3)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-muted/30 border border-border/50"></div>
          <span className="text-xs">Poor (0-1)</span>
        </div>
      </div>

      {/* 12-Month Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {yearlyData.map((monthData) => (
          <Card key={monthData.index} className="overflow-hidden">
            <CardHeader className="py-3 px-4 bg-primary/5">
              <CardTitle className="text-sm font-semibold text-center">
                {monthData.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayHeaders.map((day, i) => (
                  <div key={i} className="text-center text-xs font-medium text-muted-foreground">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before the 1st */}
                {Array.from({ length: monthData.firstDayOfWeek }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square"></div>
                ))}
                
                {/* Day cells */}
                {monthData.days.map((dayInfo) => (
                  <div
                    key={dayInfo.day}
                    className={`aspect-square flex items-center justify-center text-xs font-medium rounded ${getRatingColorClass(dayInfo.rating)}`}
                    title={`${monthData.name} ${dayInfo.day}: Rating ${dayInfo.rating}/10`}
                  >
                    {dayInfo.day}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
